import { makeStyles } from 'tss-react/mui';

import { dFlex } from '@assets/mixins';

import { Size, TabType } from '@constants/common/ui.constants';

const useStyles = makeStyles<{
  type: TabType;
  size: Size;
  isDark: boolean;
  fullWidth: boolean;
}>()((theme, { size, isDark, fullWidth }) => ({
  tabs: {
    width: 'fit-content',
    overflow: 'hidden',

    ...(fullWidth && {
      width: '100%',
    }),

    '& .MuiButtonBase-root': {
      padding: '8px 20px',

      ...(fullWidth && {
        flexGrow: 1,
      }),
    },

    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      marginLeft: 4,
    },

    '& .MuiTabs-indicator': {
      display: 'none',
    },

    '& .MuiTabs-scrollButtons': {
      display: 'flex',
    },

    ...(size === Size.Small && {
      minHeight: 40,
      maxHeight: 40,

      ...(isDark && {
        minHeight: 44,
        maxHeight: 44,
      }),

      '& .MuiButtonBase-root.MuiTab-root': {
        minHeight: 40,
        maxHeight: 40,
        fontWeight: 600,
        fontSize: 16,
        lineHeight: '20px',
        textTransform: 'initial',
      },
    }),

    ...(size === Size.Large && {
      minHeight: 56,
      maxHeight: 56,

      '& .MuiButtonBase-root.MuiTab-root': {
        minHeight: 56,
        fontWeight: 700,
        fontSize: 18,
        lineHeight: '24px',
        textTransform: 'initial',
      },
    }),
  },
  internal: {
    borderRadius: theme.borderRadius.md,
    background: theme.secondary.s1,
    transition: 'all 0.25s ease-in-out',
    padding: 0,

    ...(isDark && {
      background: theme.background.b2,
      ...dFlex(),
    }),

    '& .MuiButtonBase-root.MuiTab-root': {
      color: theme.secondary.s3,
      transition: 'all 0.25s ease-in-out',
      borderRadius: '6px',

      '&:hover': {
        color: theme.primary.p2,
      },

      '&:focus': {
        color: theme.primary.p3,
      },

      '&:active': {
        color: theme.primary.p3,
      },

      '&:disabled': {
        opacity: 0.5,
        color: theme.primary.p2,
      },
    },

    '& .Mui-selected.MuiButtonBase-root.MuiTab-root': {
      color: theme.primary.p2,
      background: theme.primary.p3,
      transition: 'all 0.25s ease-in-out',

      ...(isDark && {
        background: theme.secondary.s1,
        borderRadius: '6px',
      }),

      svg: {
        path: {
          fill: theme.primary.p2,
        },
        color: theme.primary.p2,
      },

      '& .MuiTouchRipple-root': {
        color: theme.primary.p1,
      },

      '&:hover': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },

      '&:focus': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },

      '&:active': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },
    },
  },
  external: {
    color: theme.secondary.s3,
    background: theme.secondary.s1,
    borderRadius: `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`,
    transition: 'all 0.25s ease-in-out',

    '& .Mui-selected.MuiButtonBase-root.MuiTab-root': {
      color: theme.primary.p2,
      background: theme.primary.p3,
      transition: 'all 0.25s ease-in-out',

      '& .MuiTouchRipple-root': {
        color: theme.primary.p1,
      },

      '&:hover': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },

      '&:focus': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },

      '&:active': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },
    },

    '& .MuiButtonBase-root.MuiTab-root': {
      color: theme.secondary.s3,
      border: '2px solid transparent',
      transition: 'all 0.25s ease-in-out',

      '&:first-of-type': {
        '&:active': {
          borderRadius: `${theme.borderRadius.md} 0 0 0`,
        },
      },

      '&:last-child': {
        '&:active': {
          borderRadius: `0 ${theme.borderRadius.md} 0 0`,
        },
      },

      '&:hover': {
        color: theme.primary.p2,
        background: theme.secondary.s4,
      },

      '&:focus': {
        background: theme.secondary.s4,
        color: theme.primary.p2,
      },

      '&:active': {
        color: theme.primary.p2,
        background: theme.primary.p3,
        border: `2px solid ${theme.primary.p2}`,
      },

      '&:disabled': {
        opacity: 0.4,
        color: theme.primary.p2,
        background: theme.secondary.s1,
      },
    },
  },
  embedded: {
    padding: 2,
    background: theme.background.b2,
    borderRadius: theme.borderRadius.md,

    '& .MuiButtonBase-root.MuiTab-root': {
      color: theme.secondary.s3,
      padding: '10px 8px',
      borderRadius: '6px',

      '&:hover': {
        color: theme.primary.p2,
        background: theme.secondary.s4,
      },

      '&:focus': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },

      '&:active': {
        color: theme.primary.p2,
        background: theme.primary.p3,
      },

      '&:disabled': {
        color: theme.secondary.s3,
        background: theme.background.b2,
      },
    },

    '& .Mui-selected.MuiButtonBase-root.MuiTab-root': {
      color: theme.primary.p2,
      background: theme.secondary.s1,
      borderRadius: '6px',

      '&:hover': {
        color: theme.primary.p2,
        background: theme.secondary.s1,
      },

      '&:focus': {
        color: theme.primary.p2,
        background: theme.secondary.s1,
      },

      '&:active': {
        color: theme.primary.p2,
        background: theme.secondary.s1,
      },
    },
  },
  text: {
    padding: 2,

    '& .MuiButtonBase-root.MuiTab-root': {
      padding: '20px 11px',
      minWidth: 'initial',
      fontSize: 16,
      fontWeight: 'normal',
      color: theme.secondary.s3,

      '&:hover': {
        color: theme.secondary.s4,
      },
    },

    '& .Mui-selected.MuiButtonBase-root.MuiTab-root': {
      color: theme.primary.p2,

      '&:hover': {
        color: theme.secondary.s4,
      },
    },
  },
}));

export default useStyles;
